import { ChevronRight, Home, MoreHorizontal } from 'lucide-react'

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { cn } from '@/lib/utils'

/**
 * 面包屑项
 * Requirements: 7.1
 */
export interface BreadcrumbItem {
  label: string
  path?: string
  icon?: React.ReactNode
}

/**
 * 面包屑组件属性
 */
export interface BreadcrumbProps {
  /** 面包屑项列表 */
  items: BreadcrumbItem[]
  /** 最大显示数量（超出部分折叠） */
  maxItems?: number
  /** 是否显示首页图标 */
  showHomeIcon?: boolean
  /** 自定义类名 */
  className?: string
  /** 分隔符 */
  separator?: React.ReactNode
}

/**
 * 增强面包屑组件
 * Requirements: 7.1, 7.2, 7.3, 7.4
 */
export function Breadcrumb({
  items,
  maxItems = 4,
  showHomeIcon = true,
  className,
  separator = <ChevronRight className="h-4 w-4 text-muted-foreground" />,
}: BreadcrumbProps) {
  if (items.length === 0) {
    return null
  }

  // 计算需要折叠的项
  const shouldCollapse = items.length > maxItems
  const visibleItems = shouldCollapse
    ? [items[0], ...items.slice(-2)] // 显示第一项和最后两项
    : items
  const collapsedItems = shouldCollapse ? items.slice(1, -2) : []

  /**
   * 渲染面包屑项
   * Property 10: Breadcrumb Path Rendering
   */
  const renderItem = (item: BreadcrumbItem, index: number, isLast: boolean) => {
    const content = (
      <span className="flex items-center gap-1.5">
        {index === 0 && showHomeIcon && !item.icon && (
          <Home className="h-4 w-4" />
        )}
        {item.icon}
        <span className={cn('max-w-[150px] truncate', isLast && 'font-medium')}>
          {item.label}
        </span>
      </span>
    )

    if (item.path && !isLast) {
      return (
        <a
          href={item.path}
          className="text-muted-foreground hover:text-foreground transition-colors"
        >
          {content}
        </a>
      )
    }

    return (
      <span className={cn(isLast ? 'text-foreground' : 'text-muted-foreground')}>
        {content}
      </span>
    )
  }

  return (
    <nav
      aria-label="面包屑导航"
      className={cn('flex items-center text-sm', className)}
    >
      <ol className="flex items-center gap-1.5">
        {visibleItems.map((item, index) => {
          const isLast = index === visibleItems.length - 1
          const showCollapsedMenu = shouldCollapse && index === 0

          return (
            <li key={item.path ?? item.label} className="flex items-center gap-1.5">
              {renderItem(item, index, isLast)}

              {/* 折叠菜单 */}
              {showCollapsedMenu && collapsedItems.length > 0 && (
                <>
                  <span className="mx-1">{separator}</span>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <button
                        className="flex items-center justify-center h-6 w-6 rounded hover:bg-muted transition-colors"
                        aria-label="展开更多路径"
                      >
                        <MoreHorizontal className="h-4 w-4 text-muted-foreground" />
                      </button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="start">
                      {collapsedItems.map((collapsedItem) => (
                        <DropdownMenuItem
                          key={collapsedItem.path ?? collapsedItem.label}
                          onClick={() => {
                            if (collapsedItem.path) {
                              window.location.href = collapsedItem.path
                            }
                          }}
                          className="cursor-pointer"
                        >
                          {collapsedItem.label}
                        </DropdownMenuItem>
                      ))}
                    </DropdownMenuContent>
                  </DropdownMenu>
                </>
              )}

              {!isLast && <span className="mx-1">{separator}</span>}
            </li>
          )
        })}
      </ol>
    </nav>
  )
}

/**
 * 根据路径生成面包屑项
 * Requirements: 7.1, 7.5
 */
export function generateBreadcrumbItems(
  pathname: string,
  pathTitles: Record<string, string>
): BreadcrumbItem[] {
  const segments = pathname.split('/').filter(Boolean)
  const items: BreadcrumbItem[] = []

  let currentPath = ''

  for (const segment of segments) {
    currentPath += `/${segment}`

    // 跳过认证路由前缀
    if (segment === '_authenticated') {
      continue
    }

    // 查找标题
    const title = pathTitles[currentPath]

    if (title) {
      items.push({
        label: title,
        path: currentPath,
      })
    } else {
      // 动态路由参数，尝试解析
      const dynamicTitle = getDynamicRouteTitle(segment, currentPath)
      if (dynamicTitle) {
        items.push({
          label: dynamicTitle,
          path: currentPath,
        })
      }
    }
  }

  return items
}

/**
 * 获取动态路由标题
 */
function getDynamicRouteTitle(segment: string, fullPath: string): string | null {
  // UUID 格式的 ID，返回 "详情"
  if (/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(segment)) {
    if (fullPath.includes('/projects/')) {return '项目详情'}
    if (fullPath.includes('/issues/')) {return '任务详情'}
    if (fullPath.includes('/teams/')) {return '团队详情'}
    if (fullPath.includes('/employees/')) {return '员工详情'}
    return '详情'
  }

  return null
}

/**
 * 简化面包屑组件 - 仅显示当前页面标题
 */
export function SimpleBreadcrumb({
  title,
  parentPath,
  parentTitle,
  className,
}: {
  title: string
  parentPath?: string
  parentTitle?: string
  className?: string
}) {
  const items: BreadcrumbItem[] = []

  if (parentPath && parentTitle) {
    items.push({ label: parentTitle, path: parentPath })
  }

  items.push({ label: title })

  return <Breadcrumb items={items} className={className} />
}
